import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type Status = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus]   = useState<Status>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('No verification token found in the link. Please use the link from your email.');
      return;
    }

    void fetch(`/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`, {
      // Backend redirects (3xx) on success — manual mode captures that as opaqueredirect
      redirect: 'manual',
    })
      .then(res => {
        if (res.type === 'opaqueredirect' || res.ok || (res.status >= 300 && res.status < 400)) {
          setStatus('success');
          return;
        }
        return res.json().then((body: { error?: { message?: string } }) => {
          throw new Error(body?.error?.message ?? 'Invalid or expired verification link.');
        });
      })
      .catch((err: unknown) => {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: '#1e3a5f' }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png?v=4" alt="VantixMT"
               className="w-40 sm:w-56 object-contain drop-shadow-xl" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-12 text-center">

          {status === 'loading' && (
            <>
              <div className="flex justify-center mb-5">
                <Loader2 size={48} className="text-brand-500 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Verifying your email…</h2>
              <p className="text-slate-500 text-sm">Just a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle size={36} className="text-green-500" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Email verified!</h2>
              <p className="text-slate-500 text-sm mb-8">
                Your account is now active. You can sign in and start using VantixMT.
              </p>
              <Link
                to="/login"
                className="inline-block px-6 py-2.5 bg-brand-600 text-white font-semibold rounded-lg hover:bg-brand-700 transition-colors text-sm"
              >
                Sign in
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <XCircle size={36} className="text-red-500" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Verification failed</h2>
              <p className="text-slate-500 text-sm mb-8">{message}</p>
              <Link to="/login" className="text-brand-600 hover:underline text-sm font-medium">
                Back to sign in
              </Link>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
